import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { proposalsApi, Proposal, ProposalSlot } from '../services/api';
import { parseISO, format } from 'date-fns';
import {
    Inbox,
    Clock,
    User,
    Mail,
    MessageSquare,
    Check,
    X,
    Star,
    Calendar,
    Video,
    ArrowLeft,
    CheckCircle,
    XCircle,
    AlertCircle
} from 'lucide-react';

export function ProposalsPage() {
    const [searchParams] = useSearchParams();
    const linkIdFilter = searchParams.get('link_id');

    const [proposals, setProposals] = useState<Proposal[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState<string>('pending');
    const [selectedProposal, setSelectedProposal] = useState<Proposal | null>(null);

    // Confirmation modal state
    const [confirmingProposal, setConfirmingProposal] = useState<Proposal | null>(null);
    const [selectedSlotId, setSelectedSlotId] = useState<string>('');
    const [createCalendar, setCreateCalendar] = useState(true);
    const [createTeams, setCreateTeams] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);

    // Decline modal state
    const [decliningProposal, setDecliningProposal] = useState<Proposal | null>(null);
    const [declineReason, setDeclineReason] = useState('');
    const [sendNotification, setSendNotification] = useState(true);
    const [isDeclining, setIsDeclining] = useState(false);

    // All proposals for stats (load once)
    const [allProposals, setAllProposals] = useState<Proposal[]>([]);

    useEffect(() => {
        loadProposals();
        loadAllProposalsForStats();
    }, [statusFilter, linkIdFilter]);

    const loadProposals = async () => {
        setIsLoading(true);
        try {
            const data = await proposalsApi.list(statusFilter || undefined, linkIdFilter || undefined);
            setProposals(data);
        } catch (err) {
            console.error('Failed to load proposals:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const loadAllProposalsForStats = async () => {
        try {
            const data = await proposalsApi.list(undefined, linkIdFilter || undefined);
            setAllProposals(data);
        } catch (err) {
            console.error('Failed to load all proposals:', err);
        }
    };

    const handleConfirm = async () => {
        if (!confirmingProposal || !selectedSlotId) return;

        setIsConfirming(true);
        try {
            const result = await proposalsApi.confirm(
                confirmingProposal.id,
                selectedSlotId,
                createCalendar,
                createTeams
            );

            let message = `Meeting confirmed for ${result.confirmed_time}`;
            if (result.teams_link) {
                message += `\n\nTeams link: ${result.teams_link}`;
            }
            alert(message);

            setConfirmingProposal(null);
            setSelectedSlotId('');
            setSelectedProposal(null);
            loadProposals();
            loadAllProposalsForStats();
        } catch (err: any) {
            alert(err.message || 'Failed to confirm proposal');
        } finally {
            setIsConfirming(false);
        }
    };

    const handleDecline = async () => {
        if (!decliningProposal) return;

        setIsDeclining(true);
        try {
            await proposalsApi.decline(decliningProposal.id, declineReason || undefined, sendNotification);
            setDecliningProposal(null);
            setDeclineReason('');
            setSendNotification(true);
            setSelectedProposal(null);
            loadProposals();
            loadAllProposalsForStats();
        } catch (err: any) {
            alert(err.message || 'Failed to decline proposal');
        } finally {
            setIsDeclining(false);
        }
    };

    const getStatusBadge = (status: Proposal['status']) => {
        const styles: Record<string, { bg: string; color: string }> = {
            pending: { bg: 'var(--color-primary-light)', color: 'var(--color-primary-hover)' },
            confirmed: { bg: '#dcfce7', color: '#16a34a' },
            declined: { bg: '#fee2e2', color: '#dc2626' },
            expired: { bg: 'var(--color-gray-200)', color: 'var(--color-gray-600)' }
        };
        const style = styles[status] || styles.pending;

        return (
            <span className="badge" style={{
                background: style.bg,
                color: style.color,
            }}>
                {status}
            </span>
        );
    };

    const formatSlotTime = (slot: ProposalSlot) => {
        try {
            const start = parseISO(slot.start_time_utc);
            const end = parseISO(slot.end_time_utc);
            return `${format(start, 'EEE, MMM d')} at ${format(start, 'h:mm a')} - ${format(end, 'h:mm a')}`;
        } catch {
            return slot.start_time_utc;
        }
    };

    // Calculate stats
    const pendingCount = allProposals.filter(p => p.status === 'pending').length;
    const confirmedCount = allProposals.filter(p => p.status === 'confirmed').length;
    const declinedCount = allProposals.filter(p => p.status === 'declined').length;

    if (isLoading) {
        return (
            <div className="page container">
                <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}>
                    <div className="spinner" />
                </div>
            </div>
        );
    }

    return (
        <div className="page container">
            {/* Hero Section */}
            <div className="dashboard-hero">
                <div className="dashboard-hero-content">
                    <div>
                        {linkIdFilter && (
                            <Link to="/scheduling-links" className="btn btn-ghost btn-sm" style={{
                                color: 'rgba(255,255,255,0.8)',
                                marginBottom: 'var(--space-2)',
                                display: 'inline-flex'
                            }}>
                                <ArrowLeft size={16} />
                                Back to Links
                            </Link>
                        )}
                        <h1 className="dashboard-hero-title" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <Inbox size={28} />
                            Meeting Proposals
                        </h1>
                        <p className="dashboard-hero-subtitle">
                            Review and respond to meeting requests
                        </p>
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="dashboard-stats" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <div
                    className="stat-card"
                    onClick={() => setStatusFilter('pending')}
                    style={{
                        cursor: 'pointer',
                        borderColor: statusFilter === 'pending' ? 'var(--color-primary)' : undefined
                    }}
                >
                    <div className="stat-icon" style={{
                        background: pendingCount > 0 ? '#fef3c7' : 'var(--color-gray-100)',
                        color: pendingCount > 0 ? '#f59e0b' : 'var(--color-gray-500)'
                    }}>
                        <AlertCircle size={22} />
                    </div>
                    <div className="stat-content">
                        <span className="stat-value">{pendingCount}</span>
                        <span className="stat-label">Pending</span>
                    </div>
                    {pendingCount > 0 && (
                        <span className="stat-badge">Action needed</span>
                    )}
                </div>
                <div
                    className="stat-card"
                    onClick={() => setStatusFilter('confirmed')}
                    style={{
                        cursor: 'pointer',
                        borderColor: statusFilter === 'confirmed' ? 'var(--color-primary)' : undefined
                    }}
                >
                    <div className="stat-icon" style={{ background: '#dcfce7', color: '#22c55e' }}>
                        <CheckCircle size={22} />
                    </div>
                    <div className="stat-content">
                        <span className="stat-value">{confirmedCount}</span>
                        <span className="stat-label">Confirmed</span>
                    </div>
                </div>
                <div
                    className="stat-card"
                    onClick={() => setStatusFilter('declined')}
                    style={{
                        cursor: 'pointer',
                        borderColor: statusFilter === 'declined' ? 'var(--color-primary)' : undefined
                    }}
                >
                    <div className="stat-icon" style={{ background: '#fee2e2', color: '#ef4444' }}>
                        <XCircle size={22} />
                    </div>
                    <div className="stat-content">
                        <span className="stat-value">{declinedCount}</span>
                        <span className="stat-label">Declined</span>
                    </div>
                </div>
            </div>

            {/* Filter Tabs */}
            <div className="dashboard-events-section">
                <div className="dashboard-events-header">
                    <h2>
                        {statusFilter ? statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1) : 'All'} Proposals
                    </h2>
                    <div className="dashboard-filter-tabs">
                        {['', 'pending', 'confirmed', 'declined'].map((status) => (
                            <button
                                key={status}
                                onClick={() => setStatusFilter(status)}
                                className={`filter-tab ${statusFilter === status ? 'active' : ''}`}
                            >
                                {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'All'}
                            </button>
                        ))}
                    </div>
                </div>

                {proposals.length === 0 ? (
                    <div className="empty-state-card">
                        <Inbox size={56} className="empty-state-icon" />
                        <h3>No {statusFilter || ''} proposals</h3>
                        <p>
                            {statusFilter === 'pending'
                                ? 'When someone submits a meeting request through your scheduling link, it will appear here.'
                                : 'No proposals match this filter.'}
                        </p>
                    </div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: selectedProposal ? '1fr 1fr' : '1fr', gap: 'var(--space-4)' }}>
                        {/* Proposals List */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                            {proposals.map(proposal => (
                                <div
                                    key={proposal.id}
                                    className="proposal-card"
                                    onClick={() => setSelectedProposal(proposal)}
                                    style={{
                                        background: selectedProposal?.id === proposal.id
                                            ? 'linear-gradient(135deg, var(--color-primary-light) 0%, white 100%)'
                                            : 'linear-gradient(135deg, white 0%, var(--color-gray-50) 100%)',
                                        borderColor: selectedProposal?.id === proposal.id ? 'var(--color-primary)' : undefined
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-2)' }}>
                                        <div>
                                            <div style={{ fontWeight: 600, marginBottom: '2px', color: 'var(--color-navy)' }}>
                                                {proposal.requester_name}
                                            </div>
                                            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)' }}>
                                                {proposal.requester_email}
                                            </div>
                                        </div>
                                        {getStatusBadge(proposal.status)}
                                    </div>

                                    {proposal.link && (
                                        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)', marginBottom: 'var(--space-2)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <Clock size={12} />
                                            {proposal.link.duration_minutes} min - {proposal.link.name}
                                        </div>
                                    )}

                                    {proposal.slots && proposal.slots.length > 0 && (
                                        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <Star size={12} style={{ color: 'var(--color-primary)' }} />
                                            {formatSlotTime(proposal.slots[0])}
                                            {proposal.slots.length > 1 && (
                                                <span style={{ color: 'var(--color-gray-400)' }}>
                                                    {' '}+{proposal.slots.length - 1} more
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-400)', marginTop: 'var(--space-2)', paddingTop: 'var(--space-2)', borderTop: '1px solid var(--color-gray-200)' }}>
                                        {format(parseISO(proposal.created_at), 'MMM d, yyyy \'at\' h:mm a')}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Selected Proposal Detail */}
                        {selectedProposal && (
                            <div className="proposal-detail-card">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-4)' }}>
                                    <h3 style={{ margin: 0 }}>Proposal Details</h3>
                                    <button
                                        onClick={() => setSelectedProposal(null)}
                                        className="btn btn-ghost btn-sm"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>

                                <div style={{ marginBottom: 'var(--space-4)' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                        <User size={16} style={{ color: 'var(--color-gray-400)' }} />
                                        <strong>{selectedProposal.requester_name}</strong>
                                        {getStatusBadge(selectedProposal.status)}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', color: 'var(--color-gray-500)', fontSize: 'var(--font-size-sm)' }}>
                                        <Mail size={14} />
                                        <a href={`mailto:${selectedProposal.requester_email}`}>
                                            {selectedProposal.requester_email}
                                        </a>
                                    </div>
                                </div>

                                {selectedProposal.message && (
                                    <div style={{
                                        background: 'var(--color-gray-100)',
                                        padding: 'var(--space-3)',
                                        borderRadius: 'var(--radius-md)',
                                        marginBottom: 'var(--space-4)'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-1)', fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)' }}>
                                            <MessageSquare size={14} />
                                            Message
                                        </div>
                                        <p style={{ margin: 0 }}>{selectedProposal.message}</p>
                                    </div>
                                )}

                                <h4 style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
                                    <Clock size={16} />
                                    Proposed Times
                                </h4>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
                                    {selectedProposal.slots?.map((slot, index) => (
                                        <div
                                            key={slot.id}
                                            className="proposal-time-slot"
                                            style={{
                                                background: index === 0 ? 'var(--color-primary-light)' : 'var(--color-gray-50)',
                                                borderColor: index === 0 ? 'var(--color-primary)' : 'var(--color-gray-200)'
                                            }}
                                        >
                                            {index === 0 && <Star size={14} style={{ color: 'var(--color-primary)' }} />}
                                            <span style={{
                                                fontSize: 'var(--font-size-xs)',
                                                color: 'var(--color-gray-500)',
                                                minWidth: '20px'
                                            }}>
                                                #{slot.preference_order}
                                            </span>
                                            <span style={{ flex: 1 }}>{formatSlotTime(slot)}</span>
                                        </div>
                                    ))}
                                </div>

                                {selectedProposal.status === 'pending' && (
                                    <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                                        <button
                                            onClick={() => setDecliningProposal(selectedProposal)}
                                            className="btn btn-secondary"
                                            style={{ flex: 1 }}
                                        >
                                            <X size={18} />
                                            Decline
                                        </button>
                                        <button
                                            onClick={() => {
                                                setConfirmingProposal(selectedProposal);
                                                if (selectedProposal.slots?.[0]) {
                                                    setSelectedSlotId(selectedProposal.slots[0].id);
                                                }
                                            }}
                                            className="btn btn-primary"
                                            style={{ flex: 1 }}
                                        >
                                            <Check size={18} />
                                            Confirm
                                        </button>
                                    </div>
                                )}

                                {selectedProposal.status === 'confirmed' && selectedProposal.confirmed_slot && (
                                    <div className="proposal-status-banner success">
                                        <Check size={16} />
                                        Confirmed for {format(parseISO(selectedProposal.confirmed_slot), 'EEE, MMM d \'at\' h:mm a')}
                                    </div>
                                )}

                                {selectedProposal.status === 'declined' && (
                                    <div className="proposal-status-banner error">
                                        <X size={16} />
                                        Declined
                                        {selectedProposal.declined_reason && (
                                            <p style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--font-size-sm)' }}>
                                                Reason: {selectedProposal.declined_reason}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Confirm Modal */}
            {confirmingProposal && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '450px' }}>
                        <div className="modal-header">
                            <h2>Confirm Meeting</h2>
                            <button onClick={() => { setConfirmingProposal(null); setSelectedSlotId(''); }} className="btn btn-ghost btn-sm">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Select Time</label>
                            {confirmingProposal.slots?.map((slot, index) => (
                                <label
                                    key={slot.id}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-2)',
                                        padding: 'var(--space-2)',
                                        borderRadius: 'var(--radius-sm)',
                                        cursor: 'pointer',
                                        background: selectedSlotId === slot.id ? 'var(--color-primary-light)' : 'transparent'
                                    }}
                                >
                                    <input
                                        type="radio"
                                        name="slot"
                                        checked={selectedSlotId === slot.id}
                                        onChange={() => setSelectedSlotId(slot.id)}
                                    />
                                    {index === 0 && <Star size={14} style={{ color: 'var(--color-primary)' }} />}
                                    <span>{formatSlotTime(slot)}</span>
                                </label>
                            ))}
                        </div>

                        <div className="form-group">
                            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={createCalendar}
                                    onChange={(e) => setCreateCalendar(e.target.checked)}
                                />
                                <Calendar size={16} />
                                Create calendar event
                            </label>
                        </div>

                        {createCalendar && (
                            <div className="form-group">
                                <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={createTeams}
                                        onChange={(e) => setCreateTeams(e.target.checked)}
                                    />
                                    <Video size={16} />
                                    Include Teams meeting link
                                </label>
                            </div>
                        )}

                        <div className="modal-footer" style={{ display: 'flex', gap: 'var(--space-3)' }}>
                            <button
                                onClick={() => {
                                    setConfirmingProposal(null);
                                    setSelectedSlotId('');
                                }}
                                className="btn btn-secondary"
                                style={{ flex: 1 }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirm}
                                disabled={!selectedSlotId || isConfirming}
                                className="btn btn-primary"
                                style={{ flex: 1 }}
                            >
                                {isConfirming ? 'Confirming...' : 'Confirm Meeting'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Decline Modal */}
            {/* 
                Supports "quiet decline" feature - allows declining a proposal without 
                notifying the requester. The user can uncheck "Send notification email" 
                to decline silently. This is useful when you want to decline without 
                sending an email notification.
            */}
            {decliningProposal && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '400px' }}>
                        <div className="modal-header">
                            <h2>Decline Proposal</h2>
                            <button onClick={() => { setDecliningProposal(null); setDeclineReason(''); setSendNotification(true); }} className="btn btn-ghost btn-sm">
                                <X size={20} />
                            </button>
                        </div>

                        {/* Dynamic message based on notification preference */}
                        <p style={{ color: 'var(--color-gray-500)', marginBottom: 'var(--space-4)' }}>
                            {sendNotification 
                                ? `This will notify ${decliningProposal.requester_name} that their meeting request was declined.`
                                : `This will decline the proposal without sending a notification to ${decliningProposal.requester_name}.`}
                        </p>

                        <div className="form-group">
                            <label className="form-label">Reason (optional)</label>
                            <textarea
                                className="form-textarea"
                                value={declineReason}
                                onChange={(e) => setDeclineReason(e.target.value)}
                                placeholder="e.g., Schedule conflict, please try again next week..."
                                rows={3}
                            />
                        </div>

                        {/* 
                            Quiet decline checkbox - when unchecked, the proposal will be 
                            declined without sending an email notification to the requester.
                            Defaults to checked (true) to send notifications by default.
                        */}
                        <div className="form-group">
                            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={sendNotification}
                                    onChange={(e) => setSendNotification(e.target.checked)}
                                />
                                <span>Send notification email</span>
                            </label>
                        </div>

                        <div className="modal-footer" style={{ display: 'flex', gap: 'var(--space-3)' }}>
                            <button
                                onClick={() => {
                                    setDecliningProposal(null);
                                    setDeclineReason('');
                                    setSendNotification(true);
                                }}
                                className="btn btn-secondary"
                                style={{ flex: 1 }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDecline}
                                disabled={isDeclining}
                                className="btn"
                                style={{ flex: 1, background: 'var(--color-error)', color: 'white' }}
                            >
                                {isDeclining ? 'Declining...' : 'Decline'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
